import { parseDateOnly, toDateOnly } from '@shared/utils/formatDateTime';
import { api } from '@cwd/lib/main';
import type { TaskSchedule } from '@shared/components/RecurringEditor/CronProperties';
import type {
    Issue,
    IssueReportCounts,
    IssueStatisticsLink,
    UserAgentStats,
    GeoIpTotalStats,
    TopSubscriber,
    IssueShare,
    IssueDailyStatistics,
    UnsubscribeReasonStats,
} from '../interfaces';
import type { ContactsResponse } from '@views/Lists/ContactList.access';

export type { ContactsResponse } from '@views/Lists/ContactList.access';

export interface DateRange {
    start: string | null;
    end: string | null;
}

export function buildQueryString(dateRange: DateRange, variationFilter: string | null, contactFilter: string | null = null): string {
    const parts: string[] = [];
    if (dateRange.start) {
        parts.push(`start=${encodeURIComponent(parseDateOnly(dateRange.start).toISOString())}`);
    }
    if (dateRange.end) {
        const end = parseDateOnly(dateRange.end);
        end.setDate(end.getDate() + 1);
        parts.push(`end=${encodeURIComponent(end.toISOString())}`);
    }
    if (variationFilter) {
        parts.push(`issueVariationFilter=${variationFilter}`);
    }
    if (contactFilter) {
        // NB: use a non-OData param name ('contactFilter', not '$filter') so the global
        // EnableQueryAttribute does not intercept it on IQueryable-returning aggregate endpoints.
        // The shared filter component pre-encodes for the global OData path (spaces->'+',
        // literal '+'->%2B, '#'->%23). Undo that to get the raw OData expression, then
        // encodeURIComponent the whole value so characters like '&' or '%' in values
        // (e.g. "AT&T", "50%") don't break the query string.
        const rawFilter = contactFilter.replace(/\+/g, ' ').replace(/%2B/g, '+').replace(/%23/g, '#');
        parts.push(`contactFilter=${encodeURIComponent(rawFilter)}`);
    }
    return parts.join('&');
}

export async function getContacts(issueId: string, contactType: string, qs: string, skip = 0, pageSize = 25, extra = ''): Promise<ContactsResponse> {
    const sep = qs ? '&' : '';
    return api.get(`/Issues/${issueId}/Statistics/Contacts/${contactType}?${qs}${sep}$inlinecount=allpages&$skip=${skip}&$top=${pageSize}${extra}`);
}

export function buildContactsUrl(issueId: string, contactType: string, qs: string, extra: string): string {
    const sep = qs ? '&' : '';
    return `/Issues/${issueId}/Statistics/Contacts/${contactType}?${qs}${sep}${extra}`;
}

export async function exportContactsExcel(issueId: string, contactType: string, qs: string, extra: string): Promise<{ Url: string }> {
    const url = buildContactsUrl(issueId, contactType, qs, extra);
    return api.get(`${url}&$format=excel`);
}

export async function exportContactsCsv(issueId: string, contactType: string, qs: string, extra: string): Promise<{ Url: string }> {
    const url = buildContactsUrl(issueId, contactType, qs, extra);
    return api.get(`${url}&$format=csv`);
}

export async function exportContactsToNewList(issueId: string, contactType: string, qs: string, extra: string): Promise<{ ListId: string }> {
    const url = buildContactsUrl(issueId, contactType, qs, extra);
    return api.get(`${url}&$format=list`);
}

export async function exportContactsToExistingList(issueId: string, contactType: string, qs: string, extra: string, targetListId: string): Promise<{ ListId: string }> {
    const url = buildContactsUrl(issueId, contactType, qs, extra);
    return api.get(`${url}&$format=addtolist&targetListId=${targetListId}`);
}

export async function requestExcelExportByMail(issueId: string, contactType: string, qs: string, extra: string): Promise<void> {
    const url = buildContactsUrl(issueId, contactType, qs, extra);
    return api.get(`${url}&$format=excelmail`);
}

export async function bounceContact(contactId: string, issueId: string): Promise<void> {
    return api.post(`/Contacts/${contactId}/Bounce?issueId=${issueId}`);
}

export async function unsubscribeContact(contactId: string, issueId: string): Promise<void> {
    return api.post(`/Contacts/${contactId}/Unsubscribe?issueId=${issueId}`);
}

export async function getIssue(issueId: string): Promise<Issue> {
    return api.get(`/Issues/${issueId}`);
}

export async function getOverview(issueId: string, qs: string, signal?: AbortSignal): Promise<IssueReportCounts> {
    return api.get(`/Issues/${issueId}/Statistics/Overview?${qs}`, { signal });
}

export async function getLinks(issueId: string, suppressQueryString: boolean, loadAll: boolean, qs: string, signal?: AbortSignal): Promise<IssueStatisticsLink[]> {
    const top = loadAll ? 0 : 5;
    const base = `suppressQueryString=${suppressQueryString}&top=${top}`;
    return api.get(`/Issues/${issueId}/Statistics/Links?${base}${qs ? '&' + qs : ''}`, { signal });
}

export async function getClickMapLinks(issueId: string, qs: string, signal?: AbortSignal): Promise<IssueStatisticsLink[]> {
    return api.get(`/Issues/${issueId}/Statistics/Links?suppressQueryString=true${qs ? '&' + qs : ''}`, { signal });
}

export async function getMailClients(issueId: string, qs: string, signal?: AbortSignal): Promise<UserAgentStats> {
    return api.get(`/Issues/${issueId}/Statistics/MailClients?${qs}`, { signal });
}

export async function getGeoIp(issueId: string, qs: string, signal?: AbortSignal): Promise<GeoIpTotalStats> {
    return api.get(`/Issues/${issueId}/Statistics/GeoIp?${qs}`, { signal });
}

export async function getTopSubscribers(issueId: string, qs: string, signal?: AbortSignal): Promise<TopSubscriber[]> {
    return api.get(`/Issues/${issueId}/Statistics/TopSubscribers?${qs}`, { signal });
}

export async function getShares(issueId: string, qs: string, signal?: AbortSignal): Promise<IssueShare[]> {
    return api.get(`/Issues/${issueId}/Statistics/Shares?${qs}`, { signal });
}

export async function getFirstDay(issueId: string, dateRange: DateRange, signal?: AbortSignal): Promise<IssueDailyStatistics[]> {
    const utcOffset = encodeURIComponent(new Date().getTimezoneOffset() / -60);
    // Daily stats compare against event times already shifted by utcOffset, so send plain calendar days.
    const startQs = dateRange.start ? `&start=${toDateOnly(parseDateOnly(dateRange.start))}` : '';
    const endQs = dateRange.end
        ? (() => {
              const e = parseDateOnly(dateRange.end);
              e.setDate(e.getDate() + 1);
              return `&end=${toDateOnly(e)}`;
          })()
        : '';
    return api.get(`/Issues/${issueId}/Statistics/Daily?utcOffset=${utcOffset}${startQs}${endQs}`, { signal });
}

export async function getSender(issueId: string, signal?: AbortSignal): Promise<string | null> {
    return api.get(`/Issues/${issueId}/Sender`, { signal });
}

export async function getCategoryAverages(issueId: string): Promise<IssueReportCounts> {
    return api.get(`/Issues/${issueId}/Statistics/CategoryAverages`);
}

export async function getUnsubscribeReasonStats(issueId: string, signal?: AbortSignal): Promise<UnsubscribeReasonStats[]> {
    return api.get(`/Issues/${issueId}/Statistics/UnsubscribeReasons`, { signal });
}

export interface ScheduleReminderPayload {
    issueId: string;
    subject: string;
    description: string;
    schedule: string;
    timeZone: string;
}

export async function scheduleReminder(payload: ScheduleReminderPayload): Promise<void> {
    return api.post('/Issues/RescheduleReminderToInactives', payload);
}

export async function getTimeZones(): Promise<{ TimeZoneId: string; Name: string }[]> {
    return api.get('/TimeZones');
}

export async function getDefaultTimeZone(): Promise<string> {
    return api.get('/TimeZones/Default');
}

export async function getSchedules(issueId: string): Promise<TaskSchedule[]> {
    return api.get(`/Scheduling/${issueId}/Schedules`);
}

export async function saveSchedule(issueId: string, schedule: TaskSchedule): Promise<void> {
    return api.post(`/Scheduling/${issueId}`, schedule);
}
